import React from 'react';
import classnames from 'classnames';
import { noop, find, get, isEmpty, size } from 'lodash';

// local dependencies
import { searchInput as SearchInput, searchInputPreloader as SearchInputPreloader } from 'ui/views/generic/searchInput';
import { dateRangePicker as DateRangePicker, dateRangePickerPreloader as DateRangePickerPreloader } from 'ui/views/generic/dateRangePicker';
import { table as Table, tablePreloader as TablePreloader } from 'modules/table';
import { getLabel } from 'utils/getLabel';
import { minimalTabHeader as MinimalTabHeader } from 'ui/views/widgets/minimalTabHeader';
import { cardFilter as CardFilter } from 'ui/views/widgets/cardFilter';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { tableSearchInlineWidget as TableSearchInlineWidget } from 'ui/views/widgets/tableSearchInlineWidget';
 *
 * // render
 * <TableSearchInlineWidget propName='propValue'/>
 */
export const tableSearchInlineWidget = ( props ) => {

    // dynamic class name
    const widgetClassName = classnames( 'ui-table-search-inline-widget', props.className );
    const widgetTableClassName = classnames( 'ui-table-search-inline-widget__table-container', props.classNameTable );
    
    // get search filter state and config
    const searchFilter = find( props.filters, { name: 'search' } );
    const dateRangeFilter = find( props.filters, { name: 'dateRange' } );
    const searchQuery = get( searchFilter, 'value', '' );
    const selectedDates = get( dateRangeFilter, 'value', '' );
    const title = get( props, 'widgetConfig.title', {} );

    // card filter
    const cardFilter = find( props.filters, { name: 'cardFilter' } );
    const cards = get( cardFilter, 'cards', [] );
    const cardFilterDefaultValue = get( find( cards, { selected: true } ), 'name' );
    const cardFilterValue = ! isEmpty( get( cardFilter, 'value', '' ) ) ? cardFilter.value : cardFilterDefaultValue;

    // get padding from layout config, default is 20px
    const tablePadding = get( props.layoutConfig, 'dimensions.padding', 20 );
    const searchPattern = get( props.layoutConfig, 'inputConfig.searchPattern', null );

    const titleClassName = classnames( 'ui-table-search-inline-widget__head__title', props.titleClassName );

    // minimal tab data
    const tabsConfig = get( props, 'tabsConfig', {} );
    const selectedTab = get( props, 'selectedTab', {} );

    // mail order account status info
    const mailOrderAccountStatus = get( props, 'mailOrderAccountStatusConfig.mailOrderAccountStatus' );
    const mailOrderAccountStatusVisible = get( props, 'visibility', {} )[ get( props, 'mailOrderAccountStatusConfig.visibility', '' ) ];
    
    return (
        <div className={ widgetClassName } style= { { padding: tablePadding } }>

            { props.liveListEnabled && (
                <div className='ui-table-search-inline-widget__live-list' aria-live='polite' aria-atomic='true'>
                    <button
                        type='button'
                        className='ui-table-search-inline-widget__live-list__button'
                        onClick={ props.onGetCurrentList }
                        disabled={ props.isFetching || props.isFetchingLiveList }
                    >
                        { props.isFetchingLiveList ? 'Loading current list…' : props.liveListError ? 'Retry current request' : 'Get current list' }
                    </button>
                    { props.liveListAsOf && (
                        <span className='ui-table-search-inline-widget__live-list__timestamp'>
                            { props.compactLiveListCopy
                                ? `Updated ${ new Date( props.liveListAsOf ).toLocaleString( [], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' } ) }`
                                : `Current list retrieved ${ new Date( props.liveListAsOf ).toLocaleString() }` }
                        </span>
                    ) }
                    { props.isLiveMode && (
                        <span className='ui-table-search-inline-widget__live-list__hint'>
                            { props.compactLiveListCopy ? 'View only. Reload this page to use the usual actions.' : 'Current list is for viewing. Reload this page to use the usual actions.' }
                        </span>
                    ) }
                    { props.liveListError && (
                        <span className='ui-table-search-inline-widget__live-list__error' role='alert'>
                            { props.liveListErrorMessage
                                ? `${ props.liveListErrorMessage }${ props.liveListErrorReason ? ` (Ref: ${ props.liveListErrorReason })` : '' }`
                                : props.compactLiveListCopy ? 'Could not retrieve list. Existing rows are unchanged.' : 'Could not retrieve the current list. Your rows are unchanged.' }
                        </span>
                    ) }
                </div>
            ) }

            {/* card filter */}
            {
                cardFilter ? (
                    <CardFilter
                        className='ui-table-search-inline-widget__card-filter'
                        cards={ cards }
                        value={ cardFilterValue }
                        data={ { values: props.values.cardFilter } }
                        onChange={ ( value, customizedRequestData ) => props.onFilterChange( { name: 'cardFilter', value, customizedRequestData } ) }
                    />
                ) : null
            }

            {/* description */}
            {
                props.description ? (
                    <div className='ui-table-search-inline-widget__description'>
                        { getLabel( props.description ) }
                    </div>
                ) : null
            }

            {/* head */}
            <div className='ui-table-search-inline-widget__head'>

                { !isEmpty( title ) ? (
                    <div className={ titleClassName }>
                        { getLabel( title ) }
                    </div> ) : null
                }

                { 0 < size( get( tabsConfig, 'options', [] ) ) && (
                    <MinimalTabHeader
                        tabs={ tabsConfig.options }
                        selected={ get( selectedTab, `${tabsConfig.payloadKey}`, undefined )  }
                        className= { 'ui-table-search-inline-widget__head__minimaltab' }
                        onChange={ props.onTabChange }
                    />
                ) }
                
                
                {/* filters */}
                <div className='ui-table-search-inline-widget__head__filters'>

                    {/* search input */}
                    {
                        searchFilter ? (
                            <SearchInput
                                className='ui-table-search-inline-widget__head__filters__search'
                                loading={ props.isFetching && 'filters' === props.fetchingType }
                                value={ searchQuery }
                                placeholder={ getLabel( searchFilter.placeholder ) }
                                onChange={ ( value ) => props.onFilterChange( { name: 'search', value } ) }
                                onFocus={ () => props.onDataVisibilityChange( { disable: false } ) }
                                borderless={ get( props.layoutConfig, 'styles.borderlessSearch', false ) }
                                searchPattern={ searchPattern }
                            />
                        ) : null
                    }

                    {/* date range picker */}
                    {
                        dateRangeFilter ? (
                            <DateRangePicker
                                className='ui-table-search-inline-widget__head__filters__date-range'
                                loading={ props.isFetching && 'filters' === props.fetchingType }
                                value={ selectedDates }
                                placeholder={ getLabel( dateRangeFilter.placeholder ) }
                                onChange={ ( value ) => props.onFilterChange( { name: 'dateRange', value } ) }
                                config= { dateRangeFilter.config  }
                                frequency = { props.sortAndFilter[ get( dateRangeFilter, 'config.dateRangeTypeKey' ) ] }
                                onFrequencyChange= { ( value ) => props.onSortAndFilter( { name: get( dateRangeFilter, 'config.dateRangeTypeKey' ), type: 'filter', value } ) }
                            />
                        ) : null
                    }
                </div>
            </div>

            {/* mail order account status info */}
            {
                mailOrderAccountStatus && mailOrderAccountStatusVisible ? (
                    <div className='ui-table-search-inline-widget__account-status'>

                        { getLabel( get( props, 'mailOrderAccountStatusConfig.title', {} ) ) }
                    </div>
                ) : null
            }


            {/* table */}
            <div className={ widgetTableClassName } >
                <Table
                    visibility={ props.visibility }
                    layoutConfig={ props.layoutConfig }
                    isFetching={ props.isFetching }
                    fetchingType={ props.fetchingType }
                    disablePagination={ props.disablePagination }
                    tableRowViewTemplate={ props.tableRowViewTemplate }

                    // data
                    searchQuery={ searchQuery }
                    items={ props.items }
                    columns={ props.columns }
                    sortAndFilter={ props.sortAndFilter }
                    pagination={ props.pagination }
                    searchableColumns={ props.searchatwbbleColumns }
                    actionsConfig={ props.actionsConfig }
                    isInitialData={ props.isInitialData }
                    selectedRows={ props.selectedRows }
                    options={ props.options }

                    // methods
                    onSortAndFilter={ props.onSortAndFilter }
                    onPagination={ props.onPagination }
                    onActionClick={ props.onActionClick }
                    onSelect={ props.onSelect }
                    fetchFilterSearchOptions={ props.fetchFilterSearchOptions }
                    dynamicSelectedOptions={ props.dynamicSelectedOptions }
                />
            </div>
        </div>
    );
};

// set display name
tableSearchInlineWidget.displayName = 'tableSearchInlineWidget';

// set default props
tableSearchInlineWidget.defaultProps = {
    isInitialData: true,
    isFetching: false,
    fetchingType: null,
    disablePagination: false,
    disableDataView: false,

    // static data
    visibility: {},
    widgetConfig: {},
    layoutConfig: {},
    searchableColumns: [],
    actionsConfig: {},

    // data
    items: [],
    columns: {
        primary: [],
        secondary: [],
    },
    filters: [],
    sortAndFilter: {},
    pagination: {},

    // methods
    onFilterChange: noop,
    onSortAndFilter: noop,
    onPagination: noop,
    onActionClick: noop,
    onDataVisibilityChange: noop,
    onSelect: noop,
};

// prop types
tableSearchInlineWidget.propTypes = {};

/******************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { tableSearchInlineWidgetPreloader as TableSearchInlineWidgetPreloader } from 'ui/views/widgets/tableSearchInlineWidget';
 *
 * // render
 * <TableSearchInlineWidgetPreloader propName='propValue'/>
 */
export const tableSearchInlineWidgetPreloader = ( props ) => {

    // dynamic class name
    const widgetClassName = classnames( 'ui-table-search-inline-widget-preloader', props.className );

    return (
        <div className={ widgetClassName }>
            {/* head */}
            <div className='ui-table-search-inline-widget-preloader__head'>
                <div className='ui-table-search-inline-widget-preloader__head__title' />

                {/* filters */}
                <div className='ui-table-search-inline-widget-preloader__head__filters'>
                    {/* search input */}
                    <SearchInputPreloader
                        className='ui-table-search-inline-widget-preloader__head__filters__search'
                        borderless={ false }
                    />

                    {/* date range picker */}
                    <DateRangePickerPreloader
                        className='ui-table-search-inline-widget-preloader__head__filters__date-range'
                    />
                </div>
            </div>

            {/* table */}
            <div className='ui-table-search-inline-widget-preloader__table-container'>
                <TablePreloader />
            </div>
        </div>
    );
};

// set display name
tableSearchInlineWidgetPreloader.displayName = 'tableSearchInlineWidgetPreloader';

// set default props
tableSearchInlineWidgetPreloader.defaultProps = {};

// prop types
tableSearchInlineWidgetPreloader.propTypes = {};
